import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card, Field, TextInput, SelectInput, ErrorBox, btnPrimary, btnSecondary } from "@/components/common/Form";
import { EmptyState, LoadingState } from "@/components/common/States";

const EMPTY = { address_type: "HOME", full_name: "", phone: "", address_line_1: "", address_line_2: "", landmark: "", city: "", district: "", state: "", country: "India", postal_code: "", is_default: false };
const TYPES = [["HOME", "Home"], ["OFFICE", "Office"], ["BILLING", "Billing"], ["SHIPPING", "Shipping"], ["BUSINESS", "Business"]];

export default function AddressesPage() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/addresses").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      if (editing.id) await api.put(`/addresses/${editing.id}`, editing);
      else await api.post("/addresses", editing);
      toast.success("Address saved"); setEditing(null); load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    await api.delete(`/addresses/${id}`); toast.success("Address deleted"); load();
  };
  const makeDefault = async (id) => { await api.post(`/addresses/${id}/default`); load(); };

  if (items === null) return <LoadingState />;
  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div className="space-y-6">
      <Card title="Saved addresses" subtitle="Delivery, billing and business addresses." testId="addresses-card"
        action={!editing && <button data-testid="address-add" onClick={() => setEditing({ ...EMPTY })} className={btnPrimary}><Plus size={15} /> Add address</button>}>
        {editing && (
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 mb-6 rounded-xl bg-muted/60 p-4" data-testid="address-form">
            <div className="sm:col-span-2"><ErrorBox message={error} testId="address-error" /></div>
            <Field label="Address type"><SelectInput testId="address-type" value={editing.address_type} onChange={set("address_type")} options={TYPES} /></Field>
            <Field label="Full name *"><TextInput testId="address-full-name" required value={editing.full_name} onChange={set("full_name")} /></Field>
            <Field label="Phone *"><TextInput testId="address-phone" required value={editing.phone} onChange={set("phone")} /></Field>
            <Field label="Postal code *"><TextInput testId="address-postal" required value={editing.postal_code} onChange={set("postal_code")} /></Field>
            <Field label="Address line 1 *" className="sm:col-span-2"><TextInput testId="address-line1" required value={editing.address_line_1} onChange={set("address_line_1")} /></Field>
            <Field label="Address line 2" className="sm:col-span-2"><TextInput testId="address-line2" value={editing.address_line_2} onChange={set("address_line_2")} /></Field>
            <Field label="Landmark"><TextInput testId="address-landmark" value={editing.landmark} onChange={set("landmark")} /></Field>
            <Field label="City *"><TextInput testId="address-city" required value={editing.city} onChange={set("city")} /></Field>
            <Field label="District"><TextInput testId="address-district" value={editing.district} onChange={set("district")} /></Field>
            <Field label="State *"><TextInput testId="address-state" required value={editing.state} onChange={set("state")} /></Field>
            <Field label="Country *"><TextInput testId="address-country" required value={editing.country} onChange={set("country")} /></Field>
            <label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" data-testid="address-default" checked={editing.is_default} onChange={set("is_default")} /> Set as default</label>
            <div className="sm:col-span-2 flex gap-2">
              <button disabled={saving} data-testid="address-save" className={btnPrimary}>{saving ? "Saving..." : "Save address"}</button>
              <button type="button" onClick={() => { setEditing(null); setError(""); }} data-testid="address-cancel" className={btnSecondary}>Cancel</button>
            </div>
          </form>
        )}
        {items.length === 0 && !editing ? <EmptyState title="No addresses yet" message="Add your first delivery address." /> : (
          <ul className="grid sm:grid-cols-2 gap-4" data-testid="address-list">
            {items.map((a) => (
              <li key={a.id} data-testid={`address-item-${a.id}`} className={`rounded-xl border p-4 text-sm relative ${a.is_default ? "border-primary bg-accent/40" : "border-border"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] font-semibold tracking-wide text-secondary">{a.address_type}</span>
                  {a.is_default && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary"><Star size={11} fill="currentColor" /> Default</span>}
                </div>
                <div className="font-semibold text-stone-900">{a.full_name} · {a.phone}</div>
                <div className="text-stone-600">{a.address_line_1}{a.address_line_2 && `, ${a.address_line_2}`}{a.landmark && `, ${a.landmark}`}</div>
                <div className="text-stone-600">{a.city}{a.district && `, ${a.district}`}, {a.state} {a.postal_code}, {a.country}</div>
                <div className="mt-3 flex gap-3 text-xs font-semibold">
                  <button data-testid={`address-edit-${a.id}`} onClick={() => setEditing({ ...a })} className="inline-flex items-center gap-1 text-stone-700 hover:text-secondary"><Pencil size={12} /> Edit</button>
                  {!a.is_default && <button data-testid={`address-make-default-${a.id}`} onClick={() => makeDefault(a.id)} className="inline-flex items-center gap-1 text-stone-700 hover:text-secondary"><Star size={12} /> Make default</button>}
                  <button data-testid={`address-delete-${a.id}`} onClick={() => remove(a.id)} className="inline-flex items-center gap-1 text-red-600"><Trash2 size={12} /> Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
